import { Typography } from '@material-ui/core';
import {
  FormikNumberField,
  FormikTextField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import {
  Column,
  Columns,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { formatBytes, toBytes } from '@superdispatch/ui-lab';
import { useEffect, useMemo } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { FormikMediaField } from 'shared/form/FormikMediaField';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { useCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import {
  CarrierInfoDTO,
  carrierInfoSchema,
  carrierInfoYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierInfoDTO';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { ProfileAddressForm } from 'shared/modules/carrier-profile/core/ProfileAddressForm';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import { useCarrierProfileAPI } from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { ProfileCarrierInfoDetails } from './core/ProfileCarrierInfoDetails';
import { ProfilePageLayout } from './ProfilePageLayout';
import { TransportationTypeField } from 'shared/modules/carrier-profile/core/TransportationTypeField';

const MAX_LOGO_DIMENSIONS = 1024;
const MAX_LOGO_WEIGHT_IN_MB = toBytes(1, 'mb');

export function ProfileCarrierInfoPage() {
  const carrierInfo = useCarrierInfo();
  const { data: settings } = useCarrierSettings();
  const { data: verificationStatus } = useVerifiedCarrierApplicationStatus();
  const { updateCarrierInfo } = useCarrierProfileAPI();
  const { addSnackbar } = useSnackbarStack();

  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    carrierInfoYupSchema,
    carrierInfoSchema,
  );
  const initialValues = useInitialValues(schema, carrierInfo.data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );
  const isEditingDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  const formik = useFormikEnhanced<CarrierInfoDTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: updateCarrierInfo,
    onSubmitSuccess: () => {
      addSnackbar('Carrier info was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'Carrier Info',
      });
    },
    onSubmitFailure: () =>
      addSnackbar('Failed to update carrier info', { variant: 'error' }),
  });

  useEffect(() => {
    if (carrierInfo.error) {
      addSnackbar('Failed to fetch carrier info', { variant: 'error' });
    }
  }, [addSnackbar, carrierInfo.error]);

  return (
    <>
      <DocumentTitle title="Carrier Info" />

      <ProfilePageLayout
        formik={formik}
        title="Carrier Info"
        isLoading={carrierInfo.isLoading}
      >
        <Stack space="large">
          <ProfileCarrierInfoDetails />
          <ProfileAddressForm
            label={
              <Inline space="xxsmall">
                <Typography variant="h4">Address</Typography>
                <Typography variant="h4" color="textSecondary">
                  (Required)
                </Typography>
              </Inline>
            }
            isFormDisabled={isEditingDisabled}
          />
          <Stack space="small">
            <Typography variant="h4">Contact</Typography>

            <FormikTextField
              label="Name"
              name="contact_name"
              fullWidth={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="Phone Numbers"
              name="phone_numbers"
              fullWidth={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="Email"
              name="email"
              fullWidth={true}
              disabled={isEditingDisabled}
            />
          </Stack>
          <Stack space="small">
            <Typography variant="h4">About</Typography>

            <FormikMediaField
              label="Logo"
              name="logo_url"
              accept=".jpeg, .jpg, .webp, .heic, .heif, .png"
              fullWidth={true}
              disabled={isEditingDisabled}
              maxSize={MAX_LOGO_WEIGHT_IN_MB}
              maxDimensions={MAX_LOGO_DIMENSIONS}
              helperText={`Upload logo less than ${formatBytes(
                MAX_LOGO_WEIGHT_IN_MB,
              )} and ${MAX_LOGO_DIMENSIONS}x${MAX_LOGO_DIMENSIONS} pixels or smaller.`}
              sizeError={`The logo file must be under ${formatBytes(
                MAX_LOGO_WEIGHT_IN_MB,
              )}. Try uploading a smaller image.`}
              dimensionError={`The logo must be ${MAX_LOGO_DIMENSIONS}x${MAX_LOGO_DIMENSIONS} pixels or smaller. Resize the image and try again.`}
            />

            <FormikTextField
              label="About"
              name="about"
              fullWidth={true}
              multiline={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="Website"
              name="website"
              fullWidth={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="MC Number"
              name="mc_number"
              fullWidth={true}
              disabled={isEditingDisabled}
            />
          </Stack>
          <Stack space="small">
            <Typography variant="h4">Equipment</Typography>

            <Columns>
              <Column width={['fluid', '3/5']}>
                <FormikNumberField
                  fullWidth={true}
                  label="Largest trailer capacity"
                  helperText="Max. number of sedans your largest trailer can fit. (e.g. Honda Accord)"
                  name="largest_trailer_capacity"
                  inputProps={{ decimalScale: 0, maxLength: 2 }}
                  disabled={isEditingDisabled}
                />
              </Column>
            </Columns>
          </Stack>

          <TransportationTypeField
            disabled={
              isEditingDisabled ||
              (!!carrierInfo.data?.logistics_type &&
                verificationStatus?.verified_carrier_application.status !==
                  'not_submitted')
            }
          />
        </Stack>
      </ProfilePageLayout>
    </>
  );
}
